import { useTranslation } from 'react-i18next';

import type { ClientCertificateListItem } from '@/types/api/queries';

import { Skeleton } from '@/components/ui/skeleton';

import { useClientCertificateByCodeQuery } from '@/queries';

import { centsToMoneyFormat } from '@/helpers';

import { CERTIFICATE_KIND_ICONS } from '@/constants';

import { certificateItemCounters } from './utils';

type CertificateCompositionProps = {
  certificate: ClientCertificateListItem;
  byCodeUrl: string;
};

export const CertificateComposition = (props: CertificateCompositionProps) => {
  const { certificate, byCodeUrl } = props;
  const { t } = useTranslation();

  const { data, isLoading } = useClientCertificateByCodeQuery(
    byCodeUrl,
    certificate.code,
    true,
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  const detail = data?.certificate;

  if (!detail) {
    return (
      <p className="text-muted-foreground text-sm">
        {t('No certificate with this code')}
      </p>
    );
  }

  const redeemedBy = detail.redeemed_by.map((client) => client.name).join(', ');
  const meta = [
    `${t('By whom used')}: ${redeemedBy || t('Not used')}`,
    detail.note && `${t('Note')}: ${detail.note}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-muted-foreground text-xs">{meta}</span>

      {detail.certificate_items.map((item) => {
        const Icon = CERTIFICATE_KIND_ICONS[item.kind];
        const counters = certificateItemCounters(item);

        return (
          <div
            key={item.id}
            className="bg-card grid grid-cols-[1.25rem_minmax(0,1fr)_auto_auto] items-center gap-3 rounded-md border px-2.5 py-1.5"
          >
            <Icon className="text-info-soft-foreground size-4" />
            <span className="truncate text-sm font-medium">
              {item.kind === 'deposit'
                ? t('Deposit')
                : (item.sellable?.name ?? '—')}
            </span>
            <span className="text-muted-foreground text-xs tabular-nums">
              {`${t('Issued')}: ${counters.initial} · ${t('Remaining')}: ${counters.remaining}`}
            </span>
            <span className="text-sm font-semibold tabular-nums">
              {centsToMoneyFormat(item.remaining_liability_cents)}
            </span>
          </div>
        );
      })}
    </div>
  );
};
